/* eslint-disable implicit-arrow-linebreak */
import { cx, css } from 'emotion';
import React, {
  isValidElement, cloneElement, useEffect, useState,
} from 'react';

import * as styles from './style';

const timeByVisibility = (
  visible: boolean,
  enterDuration: number,
  leaveDuration: number
) => (visible ? enterDuration : leaveDuration);
const statusByVisibility = (visible: boolean) => (visible ? 'Enter' : 'Leave');

const delayByVisibility = (
  visible: boolean,
  enterDelay: number,
  leaveDelay: number
) => (visible ? enterDelay : leaveDelay);

export type CssTransitionProps ={
  effect?: 'fade' | 'scale' | 'transform' | 'transformUp' | 'transformDown' | 'transformRight' | 'transformLeft';
  customEffect?: {
    Enter: string;
    EnterActive: string;
    Leave: string;
    LeaveActive: string;
  };
  visible: boolean;
  children: React.ReactNode;
  className?: string;
  childrenLoaded?: boolean;
  duration?: number;
  enterDelay?: number;
  enterDuration?: number;
  leaveDelay?: number;
  leaveDuration?: number;
  clearDelay?: number;
}

// TODO: proper refactor

const CssTransition = ({
  children,
  className,
  visible,
  duration = 270,
  enterDelay = 0,
  enterDuration = duration,
  leaveDelay = 0,
  leaveDuration = duration,
  clearDelay = leaveDuration / 2 + leaveDelay || 0,
  effect = 'fade',
  customEffect,
  childrenLoaded = true,
  ...props
}: CssTransitionProps) => {
  const time = timeByVisibility(visible, enterDuration, leaveDuration);
  const [ classes, setClasses ] = useState('');
  const [ renderable, setRenderable ] = useState(visible);

  useEffect(() => {
    if (!childrenLoaded) return undefined;

    const status = statusByVisibility(visible);
    const delay = delayByVisibility(visible, enterDelay, leaveDelay);

    if (visible && !renderable) {
      setRenderable(true);
    }

    setClasses(customEffect ? customEffect[status] : cx(styles[`${effect}${status}`]));

    // set class to active
    const timer = setTimeout(() => {
      setClasses((existingClasses) =>
        cx(
          existingClasses,
          css`
            transition-duration: ${time / 1000}s;
          `,
          customEffect ? customEffect[`${status}Active`] : styles[`${effect}${status}Active`]
        ));
      clearTimeout(timer);
    }, delay);

    // remove classess when animation over
    const clearClassesTimer = setTimeout(() => {
      if (!visible) {
        setClasses('');
        setRenderable(false);
      }
      clearTimeout(clearClassesTimer);
    }, time + clearDelay);

    return () => {
      clearTimeout(timer);
      clearTimeout(clearClassesTimer);
    };
  }, [
    visible,
    renderable,
    enterDelay,
    leaveDelay,
    effect,
    customEffect,
    time,
    clearDelay,
    childrenLoaded,
  ]);

  if (
    !isValidElement(children)
    || !childrenLoaded
    || (childrenLoaded && !renderable)
  ) return null;

  return cloneElement(children, {
    ...props,
    // @ts-expect-error
    className: cx(children.props.className, className, classes),
  });
};

export { CssTransition };
